import { Popconfirm } from 'antd';
import { useEffect, useState } from 'react';

import {
  cashKey,
  createCashAccount,
  createCashTransfer,
  createCashVouchersInParts,
  receiveCashTransfer,
  reconcileBank,
  recordCashCount,
  sendCashToBranch,
  setDefaultCashAccount,
  setOpeningBalance,
  undoBankReconciliation,
  updateCashAccount,
  useBankReconciliations,
  useCashCounts,
  useCashLedger,
  useCashPosition,
  useCashInTransit,
  useCashTransfers,
  useCashVouchers,
  voidCashTransfer,
  voidCashVoucher,
  voidSentCashTransfer,
  type BankReconciliation,
  type CashAccountBalance,
  type CashCount,
  type CashTransfer,
  type CashVoucher,
  useDueExpenses,
} from '@/api/cash';
import { usePayables } from '@/api/outstanding';
import { useApiMutation } from '@/api/mutations';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { IconAction } from '@/components/IconAction';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { hueClasses } from '@/components/hues';
import type { SplitPart } from '@core/cash/split';
import { formatAmount } from '@core/format/money';
import { formatDate, toIsoDate } from '@core/format/dates';
import type { Session } from '@core/session/types';
import { accountsNeedingOpeningBalance, openingBalanceNotice } from '@core/cash/position';
import {
  accountHue,
  accountRole,
  boardAccounts,
  accountState,
  boardSentence,
  dayLines,
  nextStep,
  nextStepLabel,
  topUpMove,
  type DayLine,
} from '@core/cash/board';
import { movementIn } from '@core/today/today';
import { kindIcon } from './accountIcons';
import { CashAccountPage, type CashAccountDraft } from './CashAccountPage';
import { CountAndCheckPage } from './CountAndCheckPage';
import { DayBookPrint } from './DayBookPrint';
import { CountDrawerPage } from './CountDrawerPage';
import { MoveMoneyPage, type MoveRequest } from './MoveMoneyPage';
import { inTransitLine, otherBranches } from '@core/cash/transfer';
import { accountsFor, paidFromLabel } from '@core/cash/paidFrom';
import { can, canAct, hasFeature } from '@core/session/capability';
import { closeProgress, closeTheDay, type CloseStep } from '@core/cash/closeDay';
import { dueWords } from '@core/cash/recurring';
import { MoneyOutPage } from './MoneyOutPage';
import { dayBookPrintedOn, markDayBookPrinted } from './dayBookMark';
import { CashVoucherPage } from './CashVoucherPage';
import { ReconcileBankPage } from './ReconcileBankPage';
import { clearReconcileDraft } from './reconcileDraft';
import { reconcilableAccounts } from '@core/cash/reconcile';
import {
  mayVoidVoucher,
  voucherKindWords,
  voucherKindsFor,
  type VoucherSide,
} from '@core/cash/voucher';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import { movableAccounts, type TransferDraft } from '@core/cash/transfer';
import { alreadyCounted, countableAccounts, outcomeLabels, outcomeOf, outcomeTones } from './count';

/**
 * Section 18's cash book — what is in each drawer, and every movement behind it.
 *
 * <p>
 * <strong>This screen is the last thing built in the module and the reason the first two shipped
 * with no reader.</strong> A figure from takings alone overstates by everything the shop spends; a
 * figure with no opening float is net movement since the ledger began. Both had to be true before
 * anything showed a number — and where the second still is not, the heading says so in words rather
 * than footnoting a large figure nobody reads the footnote under. See `position.ts`.
 * </p>
 */
export function CashPage({
  session,
  onOpen,
}: {
  session: Session;
  /** Opens another screen — Money out's Expense and Pay a vendor live on their own pages. */
  onOpen?: ((key: string, view?: string, find?: string, record?: number) => void) | undefined;
}) {
  const branchId = session.branch?.id;

  // The Today list: one account or all, today or every entry.
  const [accountId, setAccountId] = useState<number | undefined>(undefined);
  const [allEntries, setAllEntries] = useState(false);
  const [page, setPage] = useState(1);

  // Every sub-screen is internal state rather than in the URL, like every other one here: the shell
  // navigates by key, and putting them in the address needs route parameters nothing has yet.
  const [checking, setChecking] = useState(false);
  const [counting, setCounting] = useState<number | null>(null);
  const [editing, setEditing] = useState<'new' | number | null>(null);
  // Move money, with what is already worked out — "deposit the rest", a petty-cash top-up.
  const [moving, setMoving] = useState<Partial<TransferDraft> | null>(null);
  const [voucherSide, setVoucherSide] = useState<VoucherSide | null>(null);
  const [reconciling, setReconciling] = useState<number | null>(null);
  const [printingDayBook, setPrintingDayBook] = useState(false);
  // Money out's three kinds, before any of them opens (canvas board 3).
  const [choosingOut, setChoosingOut] = useState(false);

  // A sub-page opens at its top, and the board comes back at its own — opened from Close the day
  // far down the board, Move money otherwise opened scrolled past its accounts (2026-10-26).
  const subScreen = [
    checking,
    counting,
    editing,
    moving !== null,
    voucherSide,
    reconciling,
    printingDayBook,
    choosingOut,
  ].join();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [subScreen]);

  // The shop's date, taken once. Read from the browser at every render, a page left open overnight
  // would silently re-scope "today" under whoever is looking at it.
  const [today] = useState(() => new Date());
  const todayIso = toIsoDate(today) ?? '';
  // Whether today's day book was printed here — the one Close the day step the books cannot say.
  const [printedOn, setPrintedOn] = useState<string | null>(() =>
    dayBookPrintedOn(branchId, todayIso) ? todayIso : null,
  );

  // One read for the board: each account to the close of today, with today's in and out beside it.
  const position = useCashPosition(branchId, todayIso, todayIso);
  const ledger = useCashLedger(
    branchId,
    allEntries ? { accountId, page } : { accountId, from: todayIso, to: todayIso, page },
  );

  // Unwindowed on purpose: a card says when its drawer was last counted, and the count screen must
  // know whether today has already been counted.
  const counts = useCashCounts(branchId);
  const transfers = useCashTransfers(branchId);
  const vouchers = useCashVouchers(branchId);
  const transit = useCashInTransit(branchId);
  const statements = useBankReconciliations(branchId);
  // The Needs you card: what is due to be paid, and what is owed to suppliers — the same reads the
  // morning screen makes, for whoever may see them.
  const due = useDueExpenses(can(session, 'Expense:View') ? branchId : undefined);
  const payables = usePayables(
    branchId,
    can(session, 'Outstanding:ViewFinancials') && hasFeature(session, 'CashAndExpense'),
  );

  const accounts = position.data?.accounts ?? [];
  // The cards and chips leave out a retired account once it is empty — see boardAccounts.
  const shown = boardAccounts(accounts);
  const banks = reconcilableAccounts(accounts);
  const countable = countableAccounts(position.data);
  const kindsIn = voucherKindsFor(session, 'in');
  const kindsOut = voucherKindsFor(session, 'out');
  const mayExpense = canAct(session, 'Expense:Create') && onOpen !== undefined;
  const mayPayVendor = canAct(session, 'VendorPayment:Create') && onOpen !== undefined;

  const notice = openingBalanceNotice(position.data);
  const needFloat = accountsNeedingOpeningBalance(position.data);
  const moved = movementIn(position.data?.accounts);

  const accountName = (id: number) => accounts.find((a) => a.id === id)?.name ?? '';
  const reconciledTo = (id: number) =>
    statements.data?.items.find((s) => s.cashAccountId === id)?.statementDate;

  const count = useApiMutation({
    mutationFn: (input: {
      accountId: number;
      counted: number;
      countedOn: string;
      notes: string | null;
      /** Where the rest goes afterwards — "Record and deposit the rest". */
      deposit: Partial<TransferDraft> | null;
    }) =>
      recordCashCount(input.accountId, {
        counted: input.counted,
        countedOn: input.countedOn,
        notes: input.notes,
      }),
    success: 'Count recorded.',
    invalidates: [cashKey],
    onSuccess: (_, input) => {
      setCounting(null);
      // The deposit is its own slip, on its own page with the figures filled in: the count is an
      // observation the shop has made, the move is money somebody is about to carry to the bank.
      setMoving(input.deposit);
    },
  });

  // One voucher per account (split payments step 6); a refusal part-way keeps the page open.
  const record = useApiMutation({
    mutationFn: ({
      base,
      parts,
    }: {
      base: Parameters<typeof createCashVouchersInParts>[0];
      parts: readonly SplitPart[];
    }) => createCashVouchersInParts(base, parts),
    success: ({ saved, failed }) =>
      failed
        ? `Part saved — ${saved.length} of ${saved.length + 1} vouchers went down.`
        : saved.length === 1 && saved[0]
          ? `${saved[0].result.documentNumber} — ${formatAmount(saved[0].result.amount)} recorded.`
          : `${saved.length} vouchers — ${formatAmount(saved.reduce((sum, each) => sum + each.result.amount, 0))} recorded.`,
    invalidates: [cashKey],
    onSuccess: ({ failed }) => {
      if (!failed) {
        setVoucherSide(null);
      }
    },
  });

  const move = useApiMutation({
    mutationFn: (request: MoveRequest) =>
      request.kind === 'send' ? sendCashToBranch(request.input) : createCashTransfer(request.input),
    success: (saved) =>
      saved.betweenBranches
        ? `${saved.documentNumber} — ${formatAmount(saved.amount)} on its way to ${saved.toBranchName}.`
        : `${saved.documentNumber} — ${formatAmount(saved.amount)} moved from ${saved.fromAccountName} to ${saved.toAccountName}.`,
    invalidates: [cashKey],
    onSuccess: () => setMoving(null),
  });

  const saveAccount = useApiMutation({
    mutationFn: (draft: CashAccountDraft & { id: number | null }) =>
      draft.id === null
        ? createCashAccount({
            name: draft.name,
            kind: draft.kind,
            bankReference: draft.bankReference,
            notes: draft.notes,
            floatToKeep: draft.floatToKeep,
          })
        : updateCashAccount(draft.id, {
            name: draft.name,
            bankReference: draft.bankReference,
            notes: draft.notes,
            floatToKeep: draft.floatToKeep,
            isActive: draft.isActive,
          }),
    success: 'Account saved.',
    invalidates: [cashKey],
    onSuccess: () => setEditing(null),
  });

  const makeDefault = useApiMutation({
    mutationFn: (id: number) => setDefaultCashAccount(id),
    success: 'The branch posts here now.',
    invalidates: [cashKey],
  });

  const reconcile = useApiMutation({
    mutationFn: (input: { accountId: number } & Parameters<typeof reconcileBank>[1]) =>
      reconcileBank(input.accountId, input),
    success: (saved) =>
      `${saved.accountName} reconciled to ${formatDate(saved.statementDate)} — ${saved.lineCount} lines cleared.`,
    invalidates: [cashKey],
    onSuccess: (saved) => {
      clearReconcileDraft(saved.cashAccountId);
      setReconciling(null);
    },
  });

  if (printingDayBook) {
    return (
      <DayBookPrint
        branchId={branchId}
        accounts={shown}
        today={todayIso}
        onPrinted={(day) => {
          markDayBookPrinted(branchId, day);
          setPrintedOn(dayBookPrintedOn(branchId, todayIso) ? todayIso : null);
        }}
        onClose={() => setPrintingDayBook(false)}
      />
    );
  }

  if (reconciling !== null) {
    return (
      <ReconcileBankPage
        branchId={branchId}
        accounts={banks}
        initialAccountId={reconciling}
        busy={reconcile.isPending}
        onCancel={() => {
          clearReconcileDraft(reconciling);
          setReconciling(null);
        }}
        onSave={(id, input) => reconcile.mutate({ accountId: id, ...input })}
      />
    );
  }

  if (choosingOut) {
    return (
      <MoneyOutPage
        session={session}
        today={todayIso}
        mayExpense={mayExpense}
        mayPayVendor={mayPayVendor}
        mayDraw={kindsOut.length > 0}
        onExpense={(headId) => onOpen?.('expenses', 'new', undefined, headId)}
        onDue={(recurringId) => onOpen?.('expenses', 'due', undefined, recurringId)}
        onPayVendor={() => onOpen?.('vendorpayments', 'new')}
        onDrawings={() => {
          setChoosingOut(false);
          setVoucherSide('out');
        }}
        onMove={() => {
          setChoosingOut(false);
          setMoving({});
        }}
        onCount={() => {
          setChoosingOut(false);
          setChecking(true);
        }}
        onCancel={() => setChoosingOut(false)}
      />
    );
  }

  if (voucherSide !== null) {
    // Money out's chooser opened this for drawings; an expense and a vendor payment have their own
    // pages, so nothing else is offered here.
    return (
      <CashVoucherPage
        kinds={voucherSide === 'in' ? kindsIn : kindsOut}
        side={voucherSide}
        accounts={accounts}
        today={todayIso}
        busy={record.isPending}
        onCancel={() => setVoucherSide(null)}
        onSave={(base, parts) => record.mutateAsync({ base, parts })}
      />
    );
  }

  if (moving !== null) {
    return (
      <MoveMoneyPage
        session={session}
        today={todayIso}
        initial={moving}
        busy={move.isPending}
        onCancel={() => setMoving(null)}
        onSave={(input) => move.mutate(input)}
      />
    );
  }

  if (counting !== null) {
    return (
      <CountDrawerPage
        accounts={countable}
        allAccounts={accounts}
        initialAccountId={counting}
        countedToday={(id, day) => alreadyCounted(counts.data?.items, id, day)}
        busy={count.isPending}
        onCancel={() => setCounting(null)}
        onSave={(id, input, deposit) => count.mutate({ accountId: id, ...input, deposit })}
      />
    );
  }

  if (checking) {
    return (
      <CountAndCheckPage
        countable={countable}
        banks={banks}
        counts={counts.data?.items}
        reconciledTo={reconciledTo}
        today={todayIso}
        onCancel={() => setChecking(false)}
        onCount={(id) => {
          setChecking(false);
          setCounting(id);
        }}
        onReconcile={(id) => {
          setChecking(false);
          setReconciling(id);
        }}
      />
    );
  }

  if (editing !== null) {
    const account = editing === 'new' ? undefined : accounts.find((a) => a.id === editing);

    return (
      <CashAccountPage
        account={account}
        busy={saveAccount.isPending}
        onCancel={() => setEditing(null)}
        onSave={(draft) => saveAccount.mutate({ ...draft, id: editing === 'new' ? null : editing })}
      />
    );
  }

  const takeStep = (account: CashAccountBalance) => {
    const step = nextStep(account, counts.data?.items, todayIso);

    if (step?.kind === 'count') {
      setCounting(account.id);
    } else if (step?.kind === 'reconcile') {
      setReconciling(account.id);
    } else if (step?.kind === 'topUp') {
      setMoving(topUpMove(account, step.amount, accounts, todayIso) ?? {});
    }
  };

  const lines = dayLines(ledger.data?.items ?? [], accountName);

  const closeSteps = closeTheDay({
    accounts: position.data?.accounts,
    counts: counts.data?.items,
    today: todayIso,
    dayBookPrinted: printedOn === todayIso,
    currency: '₹',
  });

  const takeCloseStep = (step: CloseStep) => {
    if (step.id === 'count' && step.accountId !== null) {
      setCounting(step.accountId);
    } else if ((step.id === 'deposit' || step.id === 'topUp') && step.move) {
      setMoving(step.move);
    } else if (step.id === 'dayBook') {
      setPrintingDayBook(true);
    }
  };

  const columns: DataViewColumn<DayLine>[] = [
    {
      title: 'When',
      dataIndex: 'recordedAt',
      hideOnCard: true,
      render: (value: string, line: DayLine) =>
        allEntries ? (
          <span className="flex flex-col">
            <span>{formatDate(line.occurredOn)}</span>
            <span className="text-default text-[length:var(--scale-list-meta)]">
              {timeOf(value)}
            </span>
          </span>
        ) : (
          <span className="text-default tabular-nums">{timeOf(value)}</span>
        ),
    },
    {
      title: 'What',
      dataIndex: 'title',
      primary: true,
      render: (_: unknown, line: DayLine) => {
        const tone = lineTone(line);

        return (
          <span className="flex items-center gap-3">
            <span
              className={`flex size-9 shrink-0 items-center justify-center rounded-[10px] ${lineTile[tone]}`}
              aria-hidden="true"
            >
              <i className={lineIcon[tone]} />
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                {line.title}
              </span>
              <span className="text-default text-[length:var(--scale-list-meta)]">{line.meta}</span>
            </span>
          </span>
        );
      },
    },
    {
      title: 'Account',
      dataIndex: 'account',
      hideOnCard: true,
      render: (value: string) => <span className="text-default">{value}</span>,
    },
    {
      title: 'Amount',
      dataIndex: 'moneyIn',
      align: 'right',
      trailing: true,
      render: (_: unknown, line: DayLine) =>
        line.moved !== null ? (
          <span className="text-default tabular-nums">₹{formatAmount(line.moved)} moved</span>
        ) : line.moneyIn !== null ? (
          <span className="text-success tabular-nums font-semibold">
            +₹{formatAmount(line.moneyIn)}
          </span>
        ) : line.moneyOut !== null ? (
          <span className="text-danger tabular-nums font-semibold">
            −₹{formatAmount(line.moneyOut)}
          </span>
        ) : null,
    },
  ];

  const mayWriteCash = canAct(session, 'CashAndBank:Create');
  const verbs: Verb[] = [
    ...(kindsIn.length > 0
      ? [
          {
            key: 'in',
            icon: 'icon icon-arrow-down-left',
            label: 'Money in',
            hint: "Owner's money · other income",
            disabled: !mayWriteCash,
            onClick: () => setVoucherSide('in'),
          },
        ]
      : []),
    ...(kindsOut.length > 0 || mayExpense || mayPayVendor
      ? [
          {
            key: 'out',
            icon: 'icon icon-arrow-up-right',
            label: 'Money out',
            hint: 'Expense · pay a vendor · drawings',
            disabled: false,
            onClick: () => setChoosingOut(true),
          },
        ]
      : []),
    {
      key: 'move',
      icon: 'icon icon-arrow-right-left',
      label: 'Move money',
      hint: 'Till to bank · top up petty cash',
      disabled:
        !mayWriteCash ||
        (movableAccounts(accounts).length < 2 && otherBranches(session).length === 0),
      onClick: () => setMoving({}),
    },
    {
      key: 'check',
      icon: 'icon icon-clipboard-check',
      label: 'Count & check',
      hint: 'Count a drawer · match a statement',
      disabled: !mayWriteCash || (countable.length === 0 && banks.length === 0),
      onClick: () => setChecking(true),
    },
  ];

  const showsDue = can(session, 'Expense:View') && (due.data?.length ?? 0) > 0;
  const owed = payables.data?.total ?? 0;
  const needsYou =
    needFloat.length > 0 ||
    (transit.data?.incoming.length ?? 0) > 0 ||
    (transit.data?.outgoing.length ?? 0) > 0 ||
    showsDue ||
    owed > 0;

  return (
    <>
      <PageHeading
        title="Cash & bank"
        eyebrow={[longDay(today), session.branch?.name].filter(Boolean).join(' · ')}
        subtitle={boardSentence(position.data, moved, '₹')}
        overlap={shown.length > 0}
        actions={
          <Button
            icon="icon icon-printer"
            shortcut="print"
            disabled={shown.length === 0}
            onClick={() => setPrintingDayBook(true)}
          >
            Print day book
          </Button>
        }
      >
        <VerbTiles verbs={verbs} />
      </PageHeading>

      {position.data && accounts.length === 0 ? (
        <Card>
          <EmptyState
            icon="icon icon-wallet"
            title="No money has moved yet"
            hint="Take a payment or record an expense and this branch's drawer appears here."
            action={
              <WriteAction session={session} required="CashAndBank:Create">
                {(disabled) => (
                  <Button
                    disabled={disabled}
                    icon="icon icon-plus"
                    onClick={() => setEditing('new')}
                  >
                    Open an account
                  </Button>
                )}
              </WriteAction>
            }
          />
        </Card>
      ) : (
        <div className="mb-4 flex flex-col gap-2 px-4">
          <section
            aria-label="Accounts"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          >
            {shown.map((account) => (
              <AccountCard
                key={account.id}
                session={session}
                account={account}
                counts={counts.data?.items}
                reconciledTo={reconciledTo(account.id)}
                today={todayIso}
                busy={makeDefault.isPending}
                onStep={() => takeStep(account)}
                onEdit={() => setEditing(account.id)}
                onMakeDefault={() => makeDefault.mutate(account.id)}
              />
            ))}
          </section>
          <WriteAction session={session} required="CashAndBank:Create">
            {(disabled) => (
              <button
                type="button"
                disabled={disabled}
                onClick={() => setEditing('new')}
                className="text-primary cursor-pointer self-end text-[13px] font-semibold hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                + Open an account
              </button>
            )}
          </WriteAction>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {notice && (
          <Notice tone="warning" title="This is not yet a balance">
            {notice} Record what each account started with and the figure becomes what is actually
            in it.
          </Notice>
        )}

        <div className="grid grid-cols-12 items-start gap-4">
          <Card flush className="col-span-12 overflow-hidden pb-2 xl:col-span-8">
            <div className="border-border-color flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
              <div>
                <h2 className="mb-0 text-[17px] font-bold text-gray-900">
                  {allEntries ? 'All movements' : "Today's movements"}
                </h2>
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  Every payment taken, every expense and every move, as it happened.
                </p>
              </div>
              <div
                role="group"
                aria-label="Period"
                className="bg-light flex gap-1 rounded-lg p-[3px]"
              >
                {[
                  { all: false, label: 'Today' },
                  { all: true, label: 'All entries' },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={allEntries === option.all}
                    onClick={() => {
                      setAllEntries(option.all);
                      setPage(1);
                    }}
                    className={`h-[30px] cursor-pointer rounded-md px-3 text-[13px] text-gray-900 ${
                      allEntries === option.all ? 'bg-white font-semibold shadow-sm' : ''
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {shown.length > 1 && (
              <div
                role="group"
                aria-label="Account"
                className="border-border-color flex flex-wrap gap-1.5 border-b px-5 py-3"
              >
                {[{ id: undefined, name: 'All accounts' }, ...shown].map((option) => (
                  <button
                    key={option.id ?? 'all'}
                    type="button"
                    aria-pressed={accountId === option.id}
                    onClick={() => {
                      setAccountId(option.id);
                      setPage(1);
                    }}
                    className={`h-8 cursor-pointer rounded-full border px-3 text-[13px] ${
                      accountId === option.id
                        ? 'border-primary bg-primary font-semibold text-white'
                        : 'border-border-color hover:bg-light bg-white text-gray-900'
                    }`}
                  >
                    {option.name}
                  </button>
                ))}
              </div>
            )}

            <div className="px-2">
              <DataView
                rowKey="key"
                columns={columns}
                dataSource={lines}
                loading={ledger.isLoading}
                empty={
                  <EmptyState
                    icon="icon icon-list"
                    title={allEntries ? 'Nothing has moved' : 'Nothing has moved today'}
                    hint="Every payment taken, every expense and every move lands here, on the day it happened."
                  />
                }
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-3 pb-1">
              <span className="text-default text-[length:var(--scale-list-meta)]">
                {(ledger.data?.total ?? 0) === 1
                  ? '1 movement'
                  : `${ledger.data?.total ?? 0} movements`}
                {' · moves are not money in or out'}
              </span>
              {(ledger.data?.total ?? 0) > 25 && (
                <span className="flex gap-1.5">
                  <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button
                    disabled={page * 25 >= (ledger.data?.total ?? 0)}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </span>
              )}
            </div>
          </Card>

          <aside className="col-span-12 flex min-w-0 flex-col gap-4 xl:col-span-4">
            {closeSteps.length > 0 && (
              <CloseDayCard session={session} steps={closeSteps} onStep={takeCloseStep} />
            )}

            {needsYou && (
              <Card>
                <h2 className="mb-3 text-[15px] font-bold text-gray-900">Needs you</h2>
                <div className="flex flex-col gap-3">
                  {needFloat.length > 0 && (
                    <WriteAction session={session} required="CashAndBank:Create">
                      {(disabled) => (
                        <div className="flex flex-col gap-2">
                          {needFloat.map((account) => (
                            <OpeningBalanceRow
                              key={account.id}
                              account={account}
                              disabled={disabled}
                            />
                          ))}
                        </div>
                      )}
                    </WriteAction>
                  )}

                  {transit.data && (
                    <InTransitNotices
                      session={session}
                      transit={transit.data}
                      accounts={accounts}
                      today={todayIso}
                    />
                  )}

                  {showsDue && due.data?.[0] && (
                    <NeedsLine
                      name={due.data[0].name}
                      words={`₹${formatAmount(due.data[0].typicalAmount)} · ${dueWords(due.data[0].dueOn, todayIso).text.toLowerCase()}${due.data.length > 1 ? ` · ${due.data.length - 1} more` : ''}`}
                      link="Expenses →"
                      onOpen={onOpen ? () => onOpen('expenses') : undefined}
                    />
                  )}

                  {owed > 0 && (
                    <NeedsLine
                      name="Owed to suppliers"
                      words={`₹${formatAmount(owed)}`}
                      link={mayPayVendor ? 'Pay →' : 'Outstanding →'}
                      onOpen={
                        onOpen
                          ? () =>
                              mayPayVendor ? onOpen('vendorpayments', 'new') : onOpen('outstanding')
                          : undefined
                      }
                    />
                  )}
                </div>
              </Card>
            )}
          </aside>
        </div>

        {/*
          The records each verb leaves, and the only place a voucher or a move is voided, a
          statement undone or a past count read back — one card with a tab each (canvas board 1).
        */}
        <HistoryCard
          tabs={[
            {
              id: 'vouchers',
              label: 'Vouchers',
              count: vouchers.data?.items.length,
              body: (
                <VouchersCard
                  bare
                  session={session}
                  vouchers={vouchers.data?.items ?? []}
                  loading={vouchers.isLoading}
                />
              ),
            },
            {
              id: 'moves',
              label: 'Moves',
              count: transfers.data?.items.length,
              body: (
                <TransfersCard
                  bare
                  session={session}
                  transfers={transfers.data?.items ?? []}
                  loading={transfers.isLoading}
                />
              ),
            },
            {
              id: 'counts',
              label: 'Counts',
              count: counts.data?.items.length,
              body: (
                <CountsCard bare counts={counts.data?.items ?? []} loading={counts.isLoading} />
              ),
            },
            ...(banks.length > 0
              ? [
                  {
                    id: 'statements',
                    label: 'Statements',
                    count: statements.data?.items.length,
                    body: (
                      <StatementsCard
                        bare
                        session={session}
                        statements={statements.data?.items ?? []}
                        loading={statements.isLoading}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </div>
    </>
  );
}

/** "18:10" in the browser's own clock — when a row was written. */
function timeOf(instant: string): string {
  const date = new Date(instant);

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** "Wednesday 21 Oct 2026", the heading's day. */
function longDay(date: Date): string {
  return date.toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * The bank statements already matched (step 7). Only the latest of each account offers Undo, with a
 * reason — an earlier one stays fixed, so no later statement rests on a match that changed. Rendered
 * whenever the branch has a bank, empty included: a bank never reconciled is itself the finding.
 */
function StatementsCard({
  bare = false,
  session,
  statements,
  loading,
}: {
  /** Inside History: the list alone, no card or title of its own. */
  bare?: boolean;
  session: Session;
  statements: BankReconciliation[];
  loading: boolean;
}) {
  const [reason, setReason] = useState('');
  const undo = useApiMutation({
    mutationFn: ({ id, why }: { id: number; why: string }) => undoBankReconciliation(id, why),
    success: (saved) =>
      `The match to ${formatDate(saved.statementDate)} is undone; its lines are uncleared again.`,
    invalidates: [cashKey],
    onSuccess: () => setReason(''),
  });

  const columns: DataViewColumn<BankReconciliation>[] = [
    {
      title: 'Statement to',
      dataIndex: 'statementDate',
      primary: true,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Account',
      dataIndex: 'accountName',
    },
    {
      title: 'Book said',
      dataIndex: 'bookBalance',
      align: 'right',
      hideOnCard: true,
      render: (value: number) => formatAmount(value),
    },
    {
      title: 'Lines',
      dataIndex: 'lineCount',
      align: 'right',
      hideOnCard: true,
    },
    {
      title: 'Statement',
      dataIndex: 'statementBalance',
      align: 'right',
      trailing: true,
      render: (value: number, row: BankReconciliation) => (
        <span className="flex items-center justify-end gap-2">
          <span className="font-semibold text-gray-900">{formatAmount(value)}</span>
          {row.mayUndo && (
            <WriteAction session={session} required="CashAndBank:Edit">
              {(disabled) => (
                <Popconfirm
                  title="Undo this match?"
                  description={
                    <div className="w-60 pt-1">
                      <p className="text-default mb-1.5 text-[12px]">
                        Its {row.lineCount} lines go back to uncleared. Nothing in the cash book
                        changes.
                      </p>
                      <input
                        type="text"
                        placeholder="Why?"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        className={controlClass}
                      />
                    </div>
                  }
                  okText="Undo"
                  okButtonProps={{ disabled: reason.trim() === '' }}
                  onConfirm={() => undo.mutate({ id: row.id, why: reason.trim() })}
                  disabled={disabled || undo.isPending}
                >
                  <Button disabled={disabled || undo.isPending}>Undo</Button>
                </Popconfirm>
              )}
            </WriteAction>
          )}
        </span>
      ),
    },
  ];

  const list = (
    <DataView
      columns={columns}
      dataSource={statements}
      loading={loading}
      empty={
        <EmptyState
          icon="icon icon-landmark"
          title="No statement has been matched yet"
          hint="Reconcile a bank from its card above — tick what the statement shows, and it has to come out exactly."
        />
      }
    />
  );

  return bare ? (
    list
  ) : (
    <Card>
      <p className="mb-3 text-sm font-semibold text-gray-900">Bank statements</p>

      {list}
    </Card>
  );
}

/**
 * Money on the road between branches (step 5), in amber because somebody has to act: the receiving
 * branch presses Received when it arrives, choosing where it lands; the sender can call it off until
 * then. Rendered only when something is in transit.
 */
function InTransitNotices({
  session,
  transit,
  accounts,
  today,
}: {
  session: Session;
  transit: { outgoing: CashTransfer[]; incoming: CashTransfer[] };
  accounts: CashAccountBalance[];
  today: string;
}) {
  const [reason, setReason] = useState('');
  const [receivedOn, setReceivedOn] = useState(today);
  const [toAccountId, setToAccountId] = useState<number | null>(null);

  const receive = useApiMutation({
    mutationFn: (id: number) => receiveCashTransfer(id, { receivedOn, toAccountId }),
    success: (saved) =>
      `${formatAmount(saved.amount)} from ${saved.fromBranchName} received into ${saved.toAccountName ?? 'the branch'}.`,
    invalidates: [cashKey],
    onSuccess: () => setToAccountId(null),
  });

  const recall = useApiMutation({
    mutationFn: (id: number) => voidSentCashTransfer(id, reason.trim()),
    success: (saved) =>
      `${saved.documentNumber} called off; the money is back in ${saved.fromAccountName}.`,
    invalidates: [cashKey],
    onSuccess: () => setReason(''),
  });

  const choices = accounts
    .filter((account) => account.isActive)
    .map(({ id, name, kind, isDefault }) => ({ id, name, kind, isDefault }));

  if (transit.outgoing.length === 0 && transit.incoming.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      {transit.incoming.map((transfer) => {
        const usable = accountsFor(transfer.method, choices);

        return (
          <Notice key={transfer.id} tone="warning" title="Money on its way here">
            <span className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {inTransitLine(transfer, 'incoming')}{' '}
                <span className="text-default text-[11px]">{transfer.documentNumber}</span>
              </span>
              {canAct(session, 'CashAndBank:Create') && (
                <Popconfirm
                  title="Has it arrived?"
                  description={
                    <div className="flex w-64 flex-col gap-2 pt-1">
                      <Field label="Arrived on">
                        <input
                          type="date"
                          min={transfer.sentOn}
                          max={today}
                          value={receivedOn}
                          onChange={(event) => setReceivedOn(event.target.value)}
                          className={controlClass}
                        />
                      </Field>
                      {usable.length > 1 && (
                        <Field label="Into">
                          <select
                            value={toAccountId ?? ''}
                            onChange={(event) =>
                              setToAccountId(
                                event.target.value === '' ? null : Number(event.target.value),
                              )
                            }
                            className={controlClass}
                          >
                            <option value="">The branch’s usual account</option>
                            {usable.map((account) => (
                              <option key={account.id} value={account.id}>
                                {paidFromLabel(account)}
                              </option>
                            ))}
                          </select>
                        </Field>
                      )}
                    </div>
                  }
                  okText="Received"
                  okButtonProps={{ disabled: receivedOn === '' || receivedOn < transfer.sentOn }}
                  onConfirm={() => receive.mutate(transfer.id)}
                  disabled={receive.isPending}
                >
                  <Button
                    variant="primary"
                    loading={receive.isPending}
                    disabled={receive.isPending}
                  >
                    Received
                  </Button>
                </Popconfirm>
              )}
            </span>
          </Notice>
        );
      })}

      {transit.outgoing.map((transfer) => (
        <Notice key={transfer.id} tone="warning" title="Money on the road">
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {inTransitLine(transfer, 'outgoing')}{' '}
              <span className="text-default text-[11px]">{transfer.documentNumber}</span>
            </span>
            {canAct(session, 'BranchCashTransfer:Edit') && (
              <Popconfirm
                title="Call it off?"
                description={
                  <div className="w-60 pt-1">
                    <p className="text-default mb-1.5 text-[12px]">
                      The money goes back into {transfer.fromAccountName} today.
                    </p>
                    <input
                      type="text"
                      placeholder="Why?"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      className={controlClass}
                    />
                  </div>
                }
                okText="Call off"
                okButtonProps={{ disabled: reason.trim() === '' }}
                onConfirm={() => recall.mutate(transfer.id)}
                disabled={recall.isPending}
              >
                <Button disabled={recall.isPending}>Call off</Button>
              </Popconfirm>
            )}
          </span>
        </Notice>
      ))}
    </div>
  );
}

/**
 * Owner's money, drawings and other income — each voidable with a reason through its own door.
 */
function VouchersCard({
  bare = false,
  session,
  vouchers,
  loading,
}: {
  /** Inside History: the list alone, no card or title of its own. */
  bare?: boolean;
  session: Session;
  vouchers: CashVoucher[];
  loading: boolean;
}) {
  const [reason, setReason] = useState('');
  const discard = useApiMutation({
    mutationFn: ({ voucher, why }: { voucher: CashVoucher; why: string }) =>
      voidCashVoucher(voucher.id, voucher.kind, why),
    success: (saved) => `${saved.documentNumber} voided; the account is put back.`,
    invalidates: [cashKey],
    onSuccess: () => setReason(''),
  });

  const columns: DataViewColumn<CashVoucher>[] = [
    {
      title: 'Day',
      dataIndex: 'occurredOn',
      primary: true,
      render: (value: string, row: CashVoucher) => (
        <span className="flex flex-col">
          <span>{formatDate(value)}</span>
          <span className="text-default text-[11px]">{row.documentNumber}</span>
        </span>
      ),
    },
    {
      title: 'What',
      dataIndex: 'kind',
      render: (_: unknown, row: CashVoucher) => (
        <span className="flex flex-col">
          <span>
            {voucherKindWords[row.kind]}
            {row.personName ? ` — ${row.personName}` : ''}
          </span>
          {row.notes && <span className="text-default text-[11px]">{row.notes}</span>}
        </span>
      ),
    },
    {
      title: 'How',
      dataIndex: 'method',
      hideOnCard: true,
      render: (_: unknown, row: CashVoucher) =>
        `${paymentMethodLabel[row.method]}${row.cashAccountName ? ` · ${row.cashAccountName}` : ''}`,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      trailing: true,
      render: (value: number, row: CashVoucher) => (
        <span className="flex items-center justify-end gap-2">
          <span className="font-semibold text-gray-900">
            {row.kind === 'OwnerOut' ? '− ' : '+ '}
            {formatAmount(value)}
          </span>
          {mayVoidVoucher(session, row.kind) && (
            <Popconfirm
              title="Void this voucher?"
              description={
                <div className="w-60 pt-1">
                  <p className="text-default mb-1.5 text-[12px]">
                    The account takes a reversing line today.
                  </p>
                  <input
                    type="text"
                    placeholder="Why?"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    className={controlClass}
                  />
                </div>
              }
              okText="Void"
              okButtonProps={{ disabled: reason.trim() === '' }}
              onConfirm={() => discard.mutate({ voucher: row, why: reason.trim() })}
              disabled={discard.isPending}
            >
              <Button disabled={discard.isPending}>Void</Button>
            </Popconfirm>
          )}
        </span>
      ),
    },
  ];

  const list = (
    <DataView
      columns={columns}
      dataSource={vouchers}
      loading={loading}
      empty={
        <EmptyState
          icon="icon icon-wallet"
          title="No owner’s money or other income"
          hint="Money the owner puts in or takes home, and income with nothing sold behind it, lands here."
        />
      }
    />
  );

  return bare ? (
    list
  ) : (
    <Card>
      <p className="mb-3 text-sm font-semibold text-gray-900">Money in and out</p>

      {list}
    </Card>
  );
}

/**
 * The recent moves between the branch's accounts, each voidable with a reason — the only place a
 * move can be called off, so it is listed even though the movements above already show its rows.
 */
function TransfersCard({
  bare = false,
  session,
  transfers,
  loading,
}: {
  /** Inside History: the list alone, no card or title of its own. */
  bare?: boolean;
  session: Session;
  transfers: CashTransfer[];
  loading: boolean;
}) {
  const [reason, setReason] = useState('');
  const discard = useApiMutation({
    mutationFn: ({ id, why }: { id: number; why: string }) => voidCashTransfer(id, why),
    success: (saved) => `${saved.documentNumber} voided; both accounts are put back.`,
    invalidates: [cashKey],
    onSuccess: () => setReason(''),
  });

  const columns: DataViewColumn<CashTransfer>[] = [
    {
      title: 'Day',
      dataIndex: 'sentOn',
      primary: true,
      render: (value: string, row: CashTransfer) => (
        <span className="flex flex-col">
          <span>{formatDate(value)}</span>
          <span className="text-default text-[11px]">{row.documentNumber}</span>
        </span>
      ),
    },
    {
      title: 'From → to',
      dataIndex: 'fromAccountName',
      // Sent to another branch, the far end is the branch — its account is the receiver's choice.
      render: (_: unknown, row: CashTransfer) =>
        row.betweenBranches
          ? `${row.fromAccountName} → ${row.toBranchName}${row.inTransit ? ' (on its way)' : ''}`
          : `${row.fromAccountName} → ${row.toAccountName ?? ''}`,
    },
    {
      title: 'Slip',
      dataIndex: 'reference',
      hideOnCard: true,
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      align: 'right',
      trailing: true,
      render: (value: number, row: CashTransfer) => (
        <span className="flex items-center justify-end gap-2">
          <span className="font-semibold text-gray-900">{formatAmount(value)}</span>
          {/* Money sent to another branch is called off from the notice above, before it lands. */}
          {!row.betweenBranches && (
            <WriteAction session={session} required="CashAndBank:Edit">
              {(disabled) => (
                <Popconfirm
                  title="Void this move?"
                  description={
                    <div className="w-60 pt-1">
                      <p className="text-default mb-1.5 text-[12px]">
                        Both accounts take a reversing line today.
                      </p>
                      <input
                        type="text"
                        placeholder="Why?"
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        className={controlClass}
                      />
                    </div>
                  }
                  okText="Void"
                  okButtonProps={{ disabled: reason.trim() === '' }}
                  onConfirm={() => discard.mutate({ id: row.id, why: reason.trim() })}
                  disabled={disabled || discard.isPending}
                >
                  <Button disabled={disabled || discard.isPending}>Void</Button>
                </Popconfirm>
              )}
            </WriteAction>
          )}
        </span>
      ),
    },
  ];

  const list = (
    <DataView
      columns={columns}
      dataSource={transfers}
      loading={loading}
      empty={
        <EmptyState
          icon="icon icon-arrow-right-left"
          title="Nothing moved between accounts"
          hint="The evening deposit, cash drawn for the till, a petty-cash top-up — each lands here."
        />
      }
    />
  );

  return bare ? (
    list
  ) : (
    <Card>
      <p className="mb-3 text-sm font-semibold text-gray-900">Money moved</p>

      {list}
    </Card>
  );
}

/**
 * The counts already taken.
 *
 * <p>
 * <strong>It renders when empty, where the movements card above would not.</strong> "Nobody has
 * counted this drawer" is a report — the whole point of the reconciliation is that somebody does it
 * every evening, so its absence is the finding. This is the morning screen's rule about a pull-list
 * against a queue, applied to the same distinction.
 * </p>
 */
function CountsCard({
  bare = false,
  counts,
  loading,
}: {
  /** Inside History: the list alone, no card or title of its own. */
  bare?: boolean;
  counts: CashCount[];
  loading: boolean;
}) {
  const columns: DataViewColumn<CashCount>[] = [
    {
      title: 'Day',
      dataIndex: 'countedOn',
      primary: true,
      render: (value: string) => formatDate(value),
    },
    {
      title: 'Account',
      dataIndex: 'accountName',
    },
    {
      title: 'Ledger said',
      dataIndex: 'expected',
      align: 'right',
      hideOnCard: true,
      render: (value: number) => formatAmount(value),
    },
    {
      title: 'Counted',
      dataIndex: 'counted',
      align: 'right',
      render: (value: number) => formatAmount(value),
    },
    {
      title: 'Difference',
      dataIndex: 'variance',
      align: 'right',
      trailing: true,
      render: (_: unknown, row: CashCount) => {
        const outcome = outcomeOf(row.variance);

        return (
          <span className="flex flex-col items-end gap-0.5">
            <span className="font-semibold text-gray-900">
              {row.variance === 0 ? '—' : formatAmount(Math.abs(row.variance))}
            </span>
            <StatusBadge tone={outcomeTones[outcome]}>{outcomeLabels[outcome]}</StatusBadge>
          </span>
        );
      },
    },
    {
      title: 'What happened',
      dataIndex: 'notes',
      hideOnCard: true,
      render: (value: string | null) => value || '—',
    },
  ];

  const list = (
    <DataView
      columns={columns}
      dataSource={counts}
      loading={loading}
      empty={
        <EmptyState
          icon="icon icon-calculator"
          title="This drawer has never been counted"
          hint="Counting it at close is what turns the figure above from a calculation into something the shop has checked."
        />
      }
    />
  );

  return bare ? (
    list
  ) : (
    <Card>
      <p className="mb-3 text-sm font-semibold text-gray-900">Counts</p>

      {list}
    </Card>
  );
}

/**
 * One account on the board (canvas "Cash & expense flow", board 1): name and role over the tile,
 * the balance in the serif, one line on where it stands, then today's in and out beside the one next
 * step it needs — never a row of buttons. The cards rise over the band.
 *
 * A `<div>` rather than a `<button>`, per the standing rule: the actions nested here could not exist
 * inside one.
 */
function AccountCard({
  session,
  account,
  counts,
  reconciledTo,
  today,
  onStep,
  onEdit,
  onMakeDefault,
  busy,
}: {
  session: Session;
  account: CashAccountBalance;
  counts: CashCount[] | undefined;
  /** The latest statement this bank is matched to, if any. */
  reconciledTo: string | undefined;
  today: string;
  onStep: () => void;
  onEdit: () => void;
  onMakeDefault: () => void;
  busy: boolean;
}) {
  const hue = hueClasses[accountHue[account.kind]];
  const state = accountState(account, { counts, reconciledTo, today }, '₹');
  const step = nextStep(account, counts, today);

  return (
    <div className="ar-card flex flex-col gap-2.5 p-4">
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {account.name}
            {account.bankReference ? ` ${account.bankReference}` : ''}
          </p>
          <p className="text-default text-[length:var(--scale-list-meta)]">
            {accountRole(account, '₹')}
          </p>
        </div>
        <WriteAction session={session} required="CashAndBank:Edit">
          {(disabled) => (
            <IconAction
              icon="icon icon-pencil"
              label={`Edit ${account.name}`}
              disabled={disabled}
              onClick={onEdit}
            />
          )}
        </WriteAction>
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-[10px] ${hue.wash} ${hue.ink}`}
          aria-hidden="true"
        >
          <i className={kindIcon[account.kind]} />
        </span>
      </div>

      <p className="lining-nums tabular-nums font-serif text-[26px] leading-tight font-semibold text-gray-900">
        ₹{formatAmount(account.closing)}
      </p>

      {state.progress !== null && (
        <div className="bg-light h-1.5 overflow-hidden rounded-full">
          <div
            className={`h-1.5 rounded-full ${state.tone === 'warning' ? 'bg-warning' : hue.tile}`}
            style={{ width: `${Math.round(state.progress * 100)}%` }}
          />
        </div>
      )}

      <p
        className={`text-[length:var(--scale-list-meta)] ${
          state.tone === 'warning' ? 'text-warning font-semibold' : 'text-default'
        }`}
      >
        {state.text}
      </p>

      {/* Which account the branch posts to is what makes a second one of a kind a choice rather
      than a duplicate — so a non-default account offers to become it. */}
      {!account.isDefault && account.isActive && (
        <WriteAction session={session} required="CashAndBank:Edit">
          {(disabled) => (
            <button
              type="button"
              disabled={disabled || busy}
              onClick={onMakeDefault}
              className="text-default cursor-pointer self-start text-[length:var(--scale-list-meta)] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
            >
              Post here
            </button>
          )}
        </WriteAction>
      )}

      <div className="border-border-color mt-auto flex items-center justify-between gap-2 border-t pt-2.5">
        <span className="text-default tabular-nums text-[length:var(--scale-list-meta)]">
          {account.in === 0 && account.out === 0
            ? 'Nothing today'
            : [
                account.in > 0 ? `+₹${formatAmount(account.in)} in` : null,
                account.out > 0 ? `−₹${formatAmount(account.out)} out` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
        </span>
        {step && (
          <WriteAction session={session} required="CashAndBank:Create">
            {(disabled) => (
              <Button
                variant={step.kind === 'reconcile' ? 'secondary' : 'primary'}
                disabled={disabled}
                onClick={onStep}
              >
                {nextStepLabel(step, '₹')}
              </Button>
            )}
          </WriteAction>
        )}
      </div>
    </div>
  );
}

/** One of the four verbs on the band. */
interface Verb {
  key: string;
  icon: string;
  label: string;
  hint: string;
  disabled: boolean;
  onClick: () => void;
}

/**
 * The four verbs, on the band under the sentence (canvas board 1): Money in, Money out, Move money,
 * Count & check. White written out as `#ffffff` — on the band `text-white` is a token that flips in
 * dark.
 */
function VerbTiles({ verbs }: { verbs: Verb[] }) {
  return (
    <nav
      aria-label="What happened to the money"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
    >
      {verbs.map((verb) => (
        <button
          key={verb.key}
          type="button"
          disabled={verb.disabled}
          onClick={verb.onClick}
          className="flex cursor-pointer items-center gap-3 rounded-[14px] border border-[#ffffff]/20 bg-[#ffffff]/[0.08] p-3.5 text-start hover:bg-[#ffffff]/[0.14] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-[#ffffff] text-(--color-hero)"
            aria-hidden="true"
          >
            <i className={`${verb.icon} text-lg`} />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="text-[15px] font-semibold text-[#ffffff]">{verb.label}</span>
            <span className="truncate text-[12px] text-(--color-hero-muted)">{verb.hint}</span>
          </span>
        </button>
      ))}
    </nav>
  );
}

/** Which way a line on the movements list went. */
type LineTone = 'in' | 'out' | 'move';

function lineTone(line: DayLine): LineTone {
  return line.moved !== null ? 'move' : line.moneyIn !== null ? 'in' : 'out';
}

const lineTile: Record<LineTone, string> = {
  in: 'bg-success-transparent text-success',
  out: 'bg-danger-transparent text-danger',
  move: 'bg-light text-gray-900',
};

const lineIcon: Record<LineTone, string> = {
  in: 'icon icon-arrow-down-left',
  out: 'icon icon-arrow-up-right',
  move: 'icon icon-arrow-right-left',
};

/**
 * Close the day (core `cash/closeDay.ts`): the evening in order, each step opening the page that
 * already does it. The next step's button is the primary one.
 */
function CloseDayCard({
  session,
  steps,
  onStep,
}: {
  session: Session;
  steps: CloseStep[];
  onStep: (step: CloseStep) => void;
}) {
  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="mb-0 text-[15px] font-bold text-gray-900">Close the day</h2>
        <span className="text-default text-[length:var(--scale-list-meta)]">
          {closeProgress(steps)}
        </span>
      </div>
      <p className="text-default mt-0.5 mb-3 text-[length:var(--scale-list-meta)]">
        In this order, before the shutter comes down.
      </p>
      <ol className="flex flex-col gap-2.5 ps-0">
        {steps.map((step, index) => (
          <li
            key={step.id}
            className={`flex list-none items-center gap-3 rounded-xl border px-3 py-2.5 ${
              step.state === 'done'
                ? 'border-success/30 bg-success-transparent'
                : step.state === 'next'
                  ? 'border-primary/40 bg-primary-transparent'
                  : 'border-border-color bg-white'
            }`}
          >
            <span
              className={`flex size-[26px] shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${
                step.state === 'done'
                  ? 'bg-success text-white'
                  : step.state === 'next'
                    ? 'bg-primary text-white'
                    : 'bg-light text-gray-900'
              }`}
              aria-hidden="true"
            >
              {step.state === 'done' ? <i className="icon icon-check" /> : index + 1}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-[13px] font-semibold text-gray-900">
                {step.title}
                {step.state === 'done' && <span className="sr-only"> — done</span>}
              </span>
              <span className="text-default text-[12px]">{step.line}</span>
            </span>
            {step.state !== 'done' &&
              (step.id === 'dayBook' ? (
                <Button onClick={() => onStep(step)}>{step.action}</Button>
              ) : (
                <WriteAction session={session} required="CashAndBank:Create">
                  {(disabled) => (
                    <Button
                      variant={step.state === 'next' ? 'primary' : 'secondary'}
                      disabled={disabled}
                      onClick={() => onStep(step)}
                    >
                      {step.action}
                    </Button>
                  )}
                </WriteAction>
              ))}
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** A line on Needs you: a name, what it is, and where it is dealt with. */
function NeedsLine({
  name,
  words,
  link,
  onOpen,
}: {
  name: string;
  words: string;
  link: string;
  onOpen: (() => void) | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-[13px]">
      <span className="text-default min-w-0">
        <span className="font-semibold text-gray-900">{name}</span> · {words}
      </span>
      {onOpen && (
        <button
          type="button"
          onClick={onOpen}
          className="text-primary shrink-0 cursor-pointer font-semibold whitespace-nowrap hover:underline"
        >
          {link}
        </button>
      )}
    </div>
  );
}

/** One tab of History. */
interface HistoryTab {
  id: string;
  label: string;
  count: number | undefined;
  body: React.ReactNode;
}

/**
 * History — the four record lists as one card with a tab each (canvas board 1). Void and Undo still
 * live in the lists, each with its reason.
 */
function HistoryCard({ tabs }: { tabs: HistoryTab[] }) {
  const [picked, setPicked] = useState(tabs[0]?.id ?? '');
  const current = tabs.find((tab) => tab.id === picked) ?? tabs[0];

  return (
    <Card flush className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pt-4">
        <h2 className="mb-0 text-[17px] font-bold text-gray-900">History</h2>
        <span className="text-default text-[length:var(--scale-list-meta)]">
          Void and Undo live here, each with a reason.
        </span>
      </div>
      <div
        role="tablist"
        aria-label="History"
        className="border-border-color flex flex-wrap gap-1 border-b px-5 pt-2.5"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === current?.id}
            onClick={() => setPicked(tab.id)}
            className={`-mb-px h-[38px] cursor-pointer border-b-2 px-3.5 text-[13px] ${
              tab.id === current?.id
                ? 'border-primary text-primary font-semibold'
                : 'border-transparent text-gray-900'
            }`}
          >
            {tab.label}
            {tab.count !== undefined ? ` · ${tab.count}` : ''}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="p-4 lg:p-5">
        {current?.body}
      </div>
    </Card>
  );
}

/**
 * Recording what an account started with.
 *
 * <p>
 * Offered inline rather than behind a screen, because it is asked once per account and then never
 * again — the server refuses a second float, since two would both be counted and the balance would
 * be wrong by exactly the first with nothing saying so.
 * </p>
 */
function OpeningBalanceRow({
  account,
  disabled = false,
}: {
  account: CashAccountBalance;
  disabled?: boolean;
}) {
  const [amount, setAmount] = useState('');
  const [asOf, setAsOf] = useState(() => toIsoDate(new Date()) ?? '');

  const save = useApiMutation({
    mutationFn: (input: { amount: number; asOf: string }) => setOpeningBalance(account.id, input),
    success: 'Opening balance recorded.',
    invalidates: [cashKey],
  });

  const value = Number(amount);

  return (
    <div className="border-border-color flex flex-wrap items-end gap-2 rounded-md border p-3">
      <Field
        label={`Opening balance — ${account.name}`}
        className="min-w-40 flex-1"
        hint="What was in it before the ledger started. Recorded once."
      >
        <input
          type="number"
          min={0}
          step="0.01"
          disabled={disabled}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          onWheel={(event) => event.currentTarget.blur()}
          className={controlClass}
        />
      </Field>

      <Field label="As at" className="w-44">
        <input
          type="date"
          disabled={disabled}
          value={asOf}
          onChange={(event) => setAsOf(event.target.value)}
          className={controlClass}
        />
      </Field>

      <Button
        variant="primary"
        loading={save.isPending}
        disabled={
          disabled || !Number.isFinite(value) || value < 0 || amount === '' || save.isPending
        }
        onClick={() => save.mutate({ amount: value, asOf })}
      >
        Record
      </Button>
    </div>
  );
}
